// Theme / shape definitions shared by MultiCounter.vue and MultiCounterDesign.vue.
// A "preset" bundles background, border, shadow, text color, and accent color
// so users can pick a cohesive look with one click.
// The accent color is exposed as `--mc-accent` and drives the divider line and
// the small diamond-shaped ornaments around each label.

export const THEME_PRESETS = {
  // ---- New "soft / cute" packs ----
  sakura: {
    id: 'sakura',
    name: 'さくらもち',
    category: 'soft',
    background: '#FFE4EC',
    border: '4px solid #E88FA8',
    boxShadow: '0 4px 0 #FFBBD0',
    color: '#8B4C63',
    accent: '#E88FA8'
  },
  matcha: {
    id: 'matcha',
    name: 'まっちゃラテ',
    category: 'soft',
    background: '#EAF0D8',
    border: '4px solid #A4BA7B',
    boxShadow: '0 4px 0 #CCD9A8',
    color: '#4A5A32',
    accent: '#7B9A4A'
  },
  cafelatte: {
    id: 'cafelatte',
    name: 'カフェラテ',
    category: 'soft',
    background: '#F5E9D9',
    border: '4px solid #A58A65',
    boxShadow: '0 4px 0 #D9C4A5',
    color: '#6B4F34',
    accent: '#A58A65'
  },
  lavender: {
    id: 'lavender',
    name: 'ラベンダーミルク',
    category: 'soft',
    background: '#EDE5F7',
    border: '4px solid #A992C9',
    boxShadow: '0 4px 0 #C8B8E0',
    color: '#5A4877',
    accent: '#A992C9'
  },
  sumiresora: {
    id: 'sumiresora',
    name: 'すみれぞら',
    category: 'soft',
    background: '#DFF1F7',
    border: '4px solid #7BB3C9',
    boxShadow: '0 4px 0 #B5DCE8',
    color: '#355765',
    accent: '#7BB3C9'
  },
  sticker: {
    id: 'sticker',
    name: 'ステッカー',
    category: 'soft',
    background: '#FFFFFF',
    border: '5px solid #2E2E2E',
    boxShadow: '4px 4px 0 #2E2E2E',
    color: '#2E2E2E',
    accent: '#2E2E2E'
  },

  // ---- Mesh / aurora gradient packs ----
  // Multiple radial blobs layered over a base linear gradient for depth.
  // A soft white inset highlight + colored outer shadow give a "jelly" feel.
  aurora: {
    id: 'aurora',
    name: 'オーロラ',
    category: 'mesh',
    background:
      'radial-gradient(at 20% 25%, #FFD6EC 0%, transparent 55%),' +
      'radial-gradient(at 80% 30%, #C8EFFF 0%, transparent 55%),' +
      'radial-gradient(at 50% 85%, #E0CCFF 0%, transparent 55%),' +
      'linear-gradient(135deg, #FFF4F9 0%, #F2F6FF 100%)',
    border: '3px solid #FFFFFF',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.9),' +
      'inset 0 -10px 30px rgba(200,180,230,0.18),' +
      '0 10px 24px rgba(200,180,230,0.35)',
    color: '#5C4B7D',
    accent: '#B89CDE'
  },
  yuuyake: {
    id: 'yuuyake',
    name: 'ゆうやけ',
    category: 'mesh',
    background:
      'radial-gradient(at 15% 80%, #FFD1A8 0%, transparent 55%),' +
      'radial-gradient(at 85% 20%, #FFB8D1 0%, transparent 60%),' +
      'radial-gradient(at 60% 55%, #FFC9A3 0%, transparent 50%),' +
      'linear-gradient(135deg, #FFE8D6 0%, #FFD4DF 55%, #E8C5E8 100%)',
    border: '3px solid #FFFFFF',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.9),' +
      'inset 0 -10px 30px rgba(230,150,170,0.18),' +
      '0 10px 24px rgba(230,150,170,0.35)',
    color: '#7A3E52',
    accent: '#D96B8A'
  },
  yozora: {
    id: 'yozora',
    name: 'よぞら',
    category: 'mesh',
    background:
      'radial-gradient(at 20% 20%, #6A4C93 0%, transparent 55%),' +
      'radial-gradient(at 85% 30%, #C94B9E 0%, transparent 55%),' +
      'radial-gradient(at 50% 85%, #4A5CAD 0%, transparent 55%),' +
      'linear-gradient(135deg, #1E1B4B 0%, #3B2468 100%)',
    border: '3px solid rgba(255,255,255,0.28)',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.22),' +
      'inset 0 -10px 30px rgba(0,0,0,0.18),' +
      '0 10px 26px rgba(60,35,100,0.5)',
    color: '#FFFFFF',
    accent: '#E8C9FF',
    labelBg: '#E8C9FF',
    labelColor: '#1E1B4B'
  },
  wataame: {
    id: 'wataame',
    name: 'わたあめ',
    category: 'mesh',
    background:
      'radial-gradient(at 25% 25%, #FCE4F1 0%, transparent 50%),' +
      'radial-gradient(at 75% 30%, #E5E7FF 0%, transparent 50%),' +
      'radial-gradient(at 50% 85%, #FFF0CC 0%, transparent 50%),' +
      'linear-gradient(135deg, #FFFFFF 0%, #FBF5FF 100%)',
    border: '3px solid #FFFFFF',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.95),' +
      'inset 0 -8px 26px rgba(200,190,220,0.18),' +
      '0 8px 20px rgba(200,190,220,0.3)',
    color: '#7A6A88',
    accent: '#C8A7D8'
  },
  umibe: {
    id: 'umibe',
    name: 'うみべ',
    category: 'mesh',
    background:
      'radial-gradient(at 20% 30%, #BEF4FC 0%, transparent 55%),' +
      'radial-gradient(at 80% 25%, #D9F4E8 0%, transparent 55%),' +
      'radial-gradient(at 50% 85%, #C7D5F5 0%, transparent 55%),' +
      'linear-gradient(135deg, #E8F9FF 0%, #E2F1FF 100%)',
    border: '3px solid #FFFFFF',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.9),' +
      'inset 0 -10px 28px rgba(130,180,220,0.18),' +
      '0 10px 24px rgba(130,180,220,0.3)',
    color: '#2E5D7D',
    accent: '#5EADCB'
  },

  // ---- Simple ----
  transparent: {
    id: 'transparent',
    name: '透過',
    category: 'simple',
    background: 'transparent',
    color: '#555555',
    accent: '#888888'
  }
}

export const THEME_GROUPS = [
  {
    label: 'やわらかテーマ',
    ids: ['sakura', 'matcha', 'cafelatte', 'lavender', 'sumiresora', 'sticker']
  },
  {
    label: 'グラデーション',
    ids: ['aurora', 'yuuyake', 'yozora', 'wataame', 'umibe']
  },
  {
    label: 'シンプル',
    ids: ['transparent']
  }
]

export function resolveTheme(presetId) {
  return THEME_PRESETS[presetId] || THEME_PRESETS.transparent
}

// Shape: key -> border-radius + layout variant.
// variant 'box'  = square-ish (label on top / count below)
// variant 'bar'  = wide-short (label left / count right)
export const SHAPE_OPTIONS = [
  { id: 'square',  label: '四角',         radius: '0',     variant: 'box' },
  { id: 'rounded', label: '角丸',         radius: '15px',  variant: 'box' },
  { id: 'pill',    label: 'ぷっくり角丸', radius: '32px',  variant: 'box' },
  { id: 'circle',  label: '丸',           radius: '50%',   variant: 'box' },
  { id: 'bar',     label: 'スリム',       radius: '14px',  variant: 'bar' },
  { id: 'barPill', label: 'カプセル',     radius: '999px', variant: 'bar' }
]

export function shapeRadius(shapeId) {
  const s = SHAPE_OPTIONS.find(x => x.id === shapeId)
  return s ? s.radius : '0'
}

export function shapeVariant(shapeId) {
  const s = SHAPE_OPTIONS.find(x => x.id === shapeId)
  return s ? s.variant : 'box'
}

// Build style / CSS custom properties for a counter item using a preset.
// Shape radius is separate so it can combine with any preset.
// Exposes --mc-accent (divider / diamonds) and --mc-label-bg / --mc-label-color
// (floating label badge used by the bar variant).
export function buildItemStyle(presetId, shapeId, overrides = {}) {
  const preset = resolveTheme(presetId)
  const style = {
    background: preset.background,
    borderRadius: shapeRadius(shapeId)
  }
  if (preset.border) style.border = preset.border
  if (preset.boxShadow) style.boxShadow = preset.boxShadow
  if (preset.accent) style['--mc-accent'] = preset.accent

  // Label badge colors.
  // Default: theme's main text color ("color") as bg, white text.
  // Each preset may override with labelBg / labelColor for custom contrast.
  style['--mc-label-bg'] = preset.labelBg || preset.color || '#333333'
  style['--mc-label-color'] = preset.labelColor || '#FFFFFF'

  // Allow caller to override (e.g. individual textColor)
  Object.assign(style, overrides)
  return style
}

export function defaultTextColorFor(presetId) {
  const preset = resolveTheme(presetId)
  return preset.color || '#000000'
}
